/**
 * Subscription Service - Stripe integration for trial and subscription management
 * HIPAA: No PHI sent to Stripe - only user IDs and subscription status
 */

import api from './api'

// Subscription status types
export type SubscriptionStatusType = 'trialing' | 'active' | 'expired' | 'past_due' | 'canceled' | 'override'

export interface SubscriptionStatus {
  status: SubscriptionStatusType
  trial_ends_at: string | null
  days_remaining: number | null
  is_active: boolean
  is_override: boolean
  can_upgrade: boolean
}

interface CheckoutResponse {
  checkout_url: string
}

interface PortalResponse {
  portal_url: string
}

/**
 * Get current user's subscription status
 * @returns Subscription status including trial info
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  const response = await api.get<SubscriptionStatus>('/api/v1/subscription/status')
  return response.data
}

/**
 * Create Stripe Checkout session for Pro subscription
 * Redirects user to Stripe's hosted checkout page
 * @param successUrl - URL to redirect on successful payment
 * @param cancelUrl - URL to redirect on canceled payment
 * @returns Checkout URL
 */
async function createCheckoutSession(
  successUrl?: string,
  cancelUrl?: string
): Promise<string> {
  const response = await api.post<CheckoutResponse>('/api/v1/subscription/checkout', {
    success_url: successUrl,
    cancel_url: cancelUrl
  })
  return response.data.checkout_url
}

/**
 * Open Stripe Customer Portal for subscription management
 * Allows users to update payment method, cancel subscription, view invoices
 * @param returnUrl - URL to redirect after portal session
 * @returns Portal URL
 */
async function openCustomerPortal(returnUrl?: string): Promise<string> {
  const response = await api.post<PortalResponse>('/api/v1/subscription/portal', {
    return_url: returnUrl
  })
  return response.data.portal_url
}

/**
 * Redirect to Stripe Checkout for upgrade
 * Convenience function that creates session and redirects
 */
export async function redirectToCheckout(): Promise<void> {
  const currentUrl = window.location.href
  const checkoutUrl = await createCheckoutSession(
    currentUrl + '?payment=success',
    currentUrl + '?payment=canceled'
  )
  window.location.href = checkoutUrl
}

/**
 * Redirect to Stripe Customer Portal
 * Convenience function that creates portal session and redirects
 */
export async function redirectToPortal(): Promise<void> {
  const portalUrl = await openCustomerPortal(window.location.href)
  window.location.href = portalUrl
}

/**
 * Check if user can use chat features
 * @param status - Subscription status object
 * @returns true if user has active trial or subscription
 */
export function canUseChat(status: SubscriptionStatus): boolean {
  return status.is_active
}

/**
 * Get human-readable subscription status message
 * @param status - Subscription status object
 * @returns User-friendly status message
 */
export function getStatusMessage(status: SubscriptionStatus): string {
  if (status.is_override) {
    return 'Premium Access'
  }

  switch (status.status) {
    case 'trialing':
      if (status.days_remaining !== null) {
        if (status.days_remaining === 0) {
          return 'Trial ends today'
        } else if (status.days_remaining === 1) {
          return '1 day left in trial'
        }
        return `${status.days_remaining} days left in trial`
      }
      return 'Trial active'
    case 'active':
      return 'Pro subscription active'
    case 'expired':
      return 'Trial expired'
    case 'past_due':
      return 'Payment past due'
    case 'canceled':
      return 'Subscription canceled'
    default:
      return 'Unknown status'
  }
}
